import { Button, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BadgeDollarSign,
  BatteryCharging,
  CalendarCheck,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleCheckBig,
  ClipboardCheck,
  Droplets,
  Eye,
  Gauge,
  Leaf,
  LoaderCircle,
  MapPin,
  PackageCheck,
  PhoneCall,
  Quote,
  Radio,
  Send,
  ShieldCheck,
  Signal,
  Sparkles,
  Sprout,
  Star,
  Target,
  TrendingUp,
  Wifi,
  Wrench,
  X,
} from "lucide-react";
import logo from "../../assets/images/ssm.jpeg";
import api from "../../api/api.js";
import "../../css/SensorInstallModal.css";

const SensorInstallModal = ({ show, onHide, formData }) => {
  const { t, i18n } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const isRtl = i18n.language === "ar";

  const pages = [
    { key: "overview", label: t("overview_label", "Overview"), icon: Eye },
    {
      key: "features",
      label: t("features_label", "Installation"),
      icon: ClipboardCheck,
    },
    {
      key: "request",
      label: t("testimonials_label", "Request"),
      icon: Send,
    },
  ];

  useEffect(() => {
    if (show) {
      setCurrentPageIndex(0);
      setRequestError("");
      setAnimationKey((previous) => previous + 1);
    }
  }, [show]);

  useEffect(() => {
    if (show) setAnimationKey((previous) => previous + 1);
  }, [currentPageIndex, show]);

  const handleRequest = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setRequestError("");

    try {
      const response = await api.post("/sensor-installation-requests", {
        ...formData,
        status: "requested",
      });

      if (response.status !== 201 && response.data?.type !== "success") {
        throw new Error(response.data?.message || "request_failed");
      }

      onHide();
      setShowSuccess(true);
    } catch (error) {
      console.error("Sensor installation request failed:", error);
      setRequestError(
        t(
          "sensor_request_error",
          "Unable to send the request. Please try again.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToPage = (pageIndex) => {
    setCurrentPageIndex(pageIndex);
    window.setTimeout(() => {
      document
        .querySelector(".sf-modal-body")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
  };

  const benefits = [
    {
      icon: Droplets,
      tone: "water",
      title: t("benefit_water_saving_title", "Water conservation"),
      subtitle: t(
        "benefit_water_saving_subtitle",
        "Save up to 40% of irrigation water",
      ),
      metric: "-40%",
    },
    {
      icon: TrendingUp,
      tone: "growth",
      title: t("benefit_increased_yields_title", "Higher yields"),
      subtitle: t(
        "benefit_increased_yields_subtitle",
        "Improve crop production with precise monitoring",
      ),
      metric: "+25%",
    },
    {
      icon: BadgeDollarSign,
      tone: "saving",
      title: t("benefit_higher_profits_title", "Lower costs"),
      subtitle: t(
        "benefit_higher_profits_subtitle",
        "Reduce unnecessary water and field operations",
      ),
      metric: "ROI",
    },
  ];

  const installationSteps = [
    {
      icon: PackageCheck,
      title: t("step_unbox_title", "Prepare the sensor"),
      desc: t(
        "step_unbox_desc",
        "Our technician checks the sensor and prepares the installation kit.",
      ),
    },
    {
      icon: MapPin,
      title: t("step_install_title", "Choose the right location"),
      desc: t(
        "step_install_desc",
        "The best representative area and correct soil depth are selected.",
      ),
    },
    {
      icon: Wifi,
      title: t("step_connect_title", "Connect it to SmartFarm"),
      desc: t(
        "step_connect_desc",
        "The sensor is paired, configured and tested on your field.",
      ),
    },
    {
      icon: Activity,
      title: t("step_monitor_title", "Start monitoring"),
      desc: t(
        "step_monitor_desc",
        "Your measurements and irrigation recommendations become available.",
      ),
    },
  ];

  const specifications = [
    {
      icon: Gauge,
      label: t("spec_measurement", "Soil measurement"),
      value: t("spec_measurement_value", "3 moisture levels"),
    },
    {
      icon: Signal,
      label: t("spec_connectivity", "Connectivity"),
      value: t("spec_connectivity_value", "GSM / GPRS (2G)"),
    },
    {
      icon: Send,
      label: t("spec_transmission", "Transmission"),
      value: t("spec_transmission_value", "HTTP API"),
    },
    {
      icon: BatteryCharging,
      label: t("spec_power", "Power management"),
      value: t("spec_power_value", "Configurable deep sleep"),
    },
  ];

  const testimonials = [
    {
      text: t(
        "testimonial_1",
        "S.sensor transformed my irrigation efficiency from the first season.",
      ),
      name: t("testimonial_1_name", "Ahmed Ben Salem"),
      location: t("testimonial_1_location", "Kairouan, Tunisia"),
      initials: "AB",
    },
    {
      text: t(
        "testimonial_2",
        "The measurements are precise and the application is very easy to use.",
      ),
      name: t("testimonial_2_name", "Fatma Trabelsi"),
      location: t("testimonial_2_location", "Monastir, Tunisia"),
      initials: "FT",
    },
    {
      text: t(
        "testimonial_3",
        "Professional installation, reliable sensor and excellent support.",
      ),
      name: t("testimonial_3_name", "Mohamed Karray"),
      location: t("testimonial_3_location", "Sfax, Tunisia"),
      initials: "MK",
    },
  ];

  const renderSensorStage = () => (
    <div className="sf-sensor-stage" aria-label={t("product_name", "S.sensor")}>
      <div className="sf-sensor-orbit sf-sensor-orbit-one" />
      <div className="sf-sensor-orbit sf-sensor-orbit-two" />
      <div className="sf-sensor-scan-line" />
      <div className="sf-sensor-floor" />
      <div className="sf-sensor-float">
        <div className="sf-sensor-halo" />
        <img src={logo} alt={t("product_name", "S.sensor")} />
      </div>
      <div className="sf-sensor-status">
        <span className="sf-live-dot" />
        {t("sensor_online", "Ready to connect")}
      </div>
      <div className="sf-floating-chip sf-chip-signal">
        <Signal size={15} />
        {t("pill_gprs", "GSM / GPRS (2G)")}
      </div>
      <div className="sf-floating-chip sf-chip-levels">
        <Gauge size={15} />
        {t("pill_3_level", "3 monitoring levels")}
      </div>
      <Sparkles className="sf-stage-sparkle sf-stage-sparkle-one" size={18} />
      <Sparkles className="sf-stage-sparkle sf-stage-sparkle-two" size={14} />
    </div>
  );

  const renderOverviewPage = () => (
    <div className="sf-page" key={"overview-" + animationKey}>
      <section className="sf-hero-section">
        <div className="sf-hero-content">
          <div className="sf-product-kicker">
            <span>
              <Sprout size={15} />
            </span>
            Smart Farm · S.sensor
          </div>
          <h1 className="sf-hero-title">
            {t("product_name", "S.sensor")}
            <span>{t("product_tagline", "Smart farming technology")}</span>
          </h1>
          <p className="sf-hero-description">
            {t(
              "product_description",
              "Measures soil moisture at three levels, soil temperature and ambient conditions, then sends the readings to SmartFarm over GSM/GPRS.",
            )}
          </p>
          <div className="sf-feature-tags">
            <span className="sf-feature-tag">
              <Target size={16} />
              {t("pill_3_level", "3-level monitoring")}
            </span>
            <span className="sf-feature-tag">
              <Signal size={16} />
              {t("pill_gprs", "GSM / GPRS (2G)")}
            </span>
            <span className="sf-feature-tag">
              <BatteryCharging size={16} />
              {t("pill_low_power", "Low-power operation")}
            </span>
          </div>
          <button
            type="button"
            className="sf-hero-action"
            onClick={() => navigateToPage(1)}
          >
            {t("discover_how_it_works", "Discover how it works")}
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="sf-hero-visual">{renderSensorStage()}</div>
      </section>

      <section className="sf-benefits-section">
        <div className="sf-section-heading">
          <span>{t("key_benefits_heading", "Key benefits")}</span>
          <h2>
            {t("sensor_value_title", "Measure better. Irrigate smarter.")}
          </h2>
        </div>
        <div className="sf-benefits-grid">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <article
                className={"sf-benefit-card is-" + benefit.tone}
                key={benefit.title}
              >
                <div className="sf-benefit-topline">
                  <span className="sf-benefit-icon">
                    <Icon size={22} />
                  </span>
                  <strong>{benefit.metric}</strong>
                </div>
                <h3>{benefit.title}</h3>
                <p>{benefit.subtitle}</p>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );

  const renderFeaturesPage = () => (
    <div className="sf-page" key={"features-" + animationKey}>
      <div className="sf-page-header">
        <button
          type="button"
          className="sf-back-btn"
          onClick={() => navigateToPage(0)}
        >
          <ArrowLeft size={17} />
          {t("back_btn", "Back")}
        </button>
        <div>
          <span>{t("features_label", "Installation")}</span>
          <h2>{t("technical_features_title", "Features & installation")}</h2>
        </div>
      </div>

      <div className="sf-specs-grid">
        {specifications.map((specification) => {
          const Icon = specification.icon;
          return (
            <article className="sf-spec-card" key={specification.label}>
              <span className="sf-spec-icon">
                <Icon size={20} />
              </span>
              <div>
                <small>{specification.label}</small>
                <strong>{specification.value}</strong>
              </div>
            </article>
          );
        })}
      </div>

      <section className="sf-installation-section">
        <div className="sf-section-heading">
          <span>
            {t("installation_process_heading", "Installation process")}
          </span>
          <h2>{t("installation_simple_title", "Four simple steps")}</h2>
        </div>
        <div className="sf-installation-steps">
          {installationSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <article className="sf-installation-step" key={step.title}>
                <div className="sf-step-rail">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {index < installationSteps.length - 1 && <i />}
                </div>
                <div className="sf-step-icon-box">
                  <Icon size={21} />
                </div>
                <div className="sf-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <div className="sf-installation-note">
        <span>
          <Wrench size={21} />
        </span>
        <div>
          <strong>
            {t("professional_installation", "Professional installation")}
          </strong>
          <p>
            {t(
              "installation_note",
              "A certified technician handles positioning, installation, connection and final testing.",
            )}
          </p>
        </div>
        <CheckCircle2 size={22} />
      </div>
    </div>
  );

  const renderRequestPage = () => (
    <div className="sf-page" key={"request-" + animationKey}>
      <div className="sf-page-header">
        <button
          type="button"
          className="sf-back-btn"
          onClick={() => navigateToPage(1)}
        >
          <ArrowLeft size={17} />
          {t("back_btn", "Back")}
        </button>
        <div>
          <span>{t("testimonials_label", "Farmer feedback")}</span>
          <h2>{t("testimonials_title", "Trusted in the field")}</h2>
        </div>
      </div>

      <div className="sf-testimonials-grid">
        {testimonials.map((testimonial) => (
          <article className="sf-testimonial" key={testimonial.name}>
            <Quote className="sf-quote-icon" size={24} />
            <div className="sf-stars" aria-label="5/5">
              {[0, 1, 2, 3, 4].map((star) => (
                <Star key={star} size={13} fill="currentColor" />
              ))}
            </div>
            <p>{testimonial.text}</p>
            <div className="sf-testimonial-author">
              <span>{testimonial.initials}</span>
              <div>
                <strong>{testimonial.name}</strong>
                <small>
                  <MapPin size={12} />
                  {testimonial.location}
                </small>
              </div>
            </div>
          </article>
        ))}
      </div>

      <section className="sf-request-panel">
        <div className="sf-request-decoration sf-request-decoration-one" />
        <div className="sf-request-decoration sf-request-decoration-two" />
        <div className="sf-request-content">
          <span className="sf-request-icon">
            <Leaf size={24} />
          </span>
          <div>
            <small>{t("product_name", "S.sensor")}</small>
            <h2>{t("ready_to_transform", "Ready to equip your field?")}</h2>
            <p>
              {t(
                "join_successful_farmers",
                "Send your request and our team will contact you to arrange the installation.",
              )}
            </p>
          </div>
        </div>
        <div className="sf-request-assurances">
          <span>
            <PhoneCall size={15} />
            {t("confirmation_call", "Confirmation within 24 hours")}
          </span>
          <span>
            <CalendarCheck size={15} />
            {t("installation_scheduling", "Flexible scheduling")}
          </span>
          <span>
            <ShieldCheck size={15} />
            {t("spec_warranty_value", "3-year warranty")}
          </span>
        </div>
        {requestError && (
          <div className="sf-request-error" role="alert">
            {requestError}
          </div>
        )}
        <Button
          className="sf-request-btn"
          onClick={handleRequest}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <LoaderCircle className="sf-loading-icon" size={19} />
              {t("processing_request", "Sending request...")}
            </>
          ) : (
            <>
              <Send size={18} />
              {t("request_installation_btn", "Request installation")}
            </>
          )}
        </Button>
      </section>
    </div>
  );

  const renderCurrentPage = () => {
    if (currentPageIndex === 1) return renderFeaturesPage();
    if (currentPageIndex === 2) return renderRequestPage();
    return renderOverviewPage();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={isLoading ? undefined : onHide}
        centered
        size="xl"
        className="sf-modal"
        backdrop="static"
        keyboard={!isLoading}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="sf-modal-wrapper">
          <button
            type="button"
            className="sf-close-btn"
            onClick={onHide}
            disabled={isLoading}
            aria-label={t("close", "Close")}
          >
            <X size={20} />
          </button>

          <nav className="sf-progress-container" aria-label="Installation">
            <div className="sf-progress-brand">
              <span>
                <Radio size={18} />
              </span>
              <div>
                <strong>S.sensor</strong>
                <small>Smart Farm</small>
              </div>
            </div>
            <div className="sf-progress-indicator">
              {pages.map((page, index) => {
                const StepIcon = page.icon;
                const isActive = currentPageIndex === index;
                const isCompleted = currentPageIndex > index;
                return (
                  <button
                    type="button"
                    key={page.key}
                    className={
                      "sf-progress-item " +
                      (isActive ? "is-active " : "") +
                      (isCompleted ? "is-completed" : "")
                    }
                    onClick={() => navigateToPage(index)}
                  >
                    <span className="sf-step-circle">
                      {isCompleted ? (
                        <Check size={16} />
                      ) : (
                        <StepIcon size={16} />
                      )}
                    </span>
                    <span className="sf-step-label">{page.label}</span>
                    {index < pages.length - 1 && (
                      <span className="sf-progress-connector" />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="sf-modal-body">{renderCurrentPage()}</div>

          {currentPageIndex < pages.length - 1 && (
            <footer className="sf-modal-footer">
              <span>
                {currentPageIndex + 1} / {pages.length}
              </span>
              <div>
                {currentPageIndex > 0 && (
                  <Button
                    className="sf-footer-back"
                    onClick={() => navigateToPage(currentPageIndex - 1)}
                  >
                    <ArrowLeft size={17} />
                    {t("back_btn", "Back")}
                  </Button>
                )}
                <Button
                  className="sf-next-btn"
                  onClick={() => navigateToPage(currentPageIndex + 1)}
                >
                  {t("continue_btn", "Continue")}
                  <ArrowRight size={17} />
                </Button>
              </div>
            </footer>
          )}
        </div>
      </Modal>

      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        className="sf-success-modal"
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="sf-success-content">
          <div className="sf-success-orbit">
            <CircleCheckBig size={38} />
          </div>
          <span className="sf-success-kicker">
            {t("request_sent_success", "Request sent successfully")}
          </span>
          <h2>{t("thank_you", "Thank you!")}</h2>
          <p>
            {t(
              "success_message",
              "We received your request and will contact you soon to schedule your S.sensor installation.",
            )}
          </p>
          <div className="sf-next-steps">
            <span>
              <PhoneCall size={18} />
              {t("confirmation_call", "Confirmation call within 24 hours")}
            </span>
            <span>
              <CalendarCheck size={18} />
              {t("installation_scheduling", "Installation scheduling")}
            </span>
            <span>
              <Wrench size={18} />
              {t("professional_setup", "Professional setup at your farm")}
            </span>
          </div>
          <Button
            className="sf-success-close-btn"
            onClick={() => setShowSuccess(false)}
          >
            <Check size={18} />
            {t("perfect_btn", "Perfect!")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default SensorInstallModal;
